import { IconButton, Menu, MenuItem, ThreeDotsIcon, Divider } from "@flat-ui";
import { Box } from "@mui-wrapper";
import { useRef, useState } from "react";

/**
 * Keeps key presses inside a dialog that a menu item opened away from the menu. The dialog is
 * portalled out of the menu in the DOM, but React still bubbles its events through the menu,
 * whose keyboard handling would otherwise close it on Tab, taking the dialog along, and swallow
 * arrow keys and letters for its own navigation. Sub-menus are portalled too, but stay menus.
 */
function stopKeyDownFromPortal(event) {
  const target = event.target;
  if (!event.currentTarget.contains(target) && target.closest('[role="dialog"]')) {
    event.stopPropagation();
  }
}
/**
 * Renders a three-dots menu button for list items and handles its open/close state.
 * Expects a list of menu items with label, icon and click handler; renders nothing else.
 *
 * @template T - The type of item being operated on
 */
export function ListItemMenu({
  item,
  menuItems
}) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef(null);
  const visibleMenuItems = menuItems.filter(menuItem => menuItem.shouldShow ? menuItem.shouldShow(item) : true);

  /** Resolves whether a divider should be shown after a menu item */
  function shouldShowDivider(menuItem_0, index) {
    // A divider after the last item would dangle at the bottom of the menu, which happens
    // whenever the items it was meant to separate are hidden.
    if (index === visibleMenuItems.length - 1) {
      return false;
    }
    return typeof menuItem_0.shouldShowDividerAfter === "function" ? menuItem_0.shouldShowDividerAfter(item) : !!menuItem_0.shouldShowDividerAfter;
  }
  return <Box data-testid="three-dot-menu" onClick={e => {
    e.stopPropagation();
  }}>
      <IconButton ref={buttonRef} onClick={e_0 => {
      e_0.stopPropagation();
      e_0.preventDefault();
      setIsOpen(true);
    }} size="s">
        <ThreeDotsIcon />
      </IconButton>

      <Menu open={isOpen} anchorEl={buttonRef.current} onClose={() => setIsOpen(false)}>
        {visibleMenuItems.map((menuItem_1, idx) => {
        let content;
        if (menuItem_1.component) {
          content = typeof menuItem_1.component === "function" ? menuItem_1.component(item, () => setIsOpen(false)) : menuItem_1.component;
        } else {
          const hrefUrl = menuItem_1.getHrefUrl?.(item);
          content =
          // Use capture phase to intercept left-clicks before FaroMenuItem's internal
          // navigate(href), which misroutes external URLs as relative paths.
          // Middle-click and right-click are unaffected since they don't fire "click".
          <Box onClickCapture={menuItem_1.shouldHandleNavigation ? e_1 => {
            e_1.stopPropagation();
            e_1.preventDefault();
            menuItem_1.onSelect?.(item);
            setIsOpen(false);
          } : undefined}>
                <MenuItem label={menuItem_1.label} href={hrefUrl} onClick={() => {
              menuItem_1.onSelect?.(item);
              setIsOpen(false);
            }} Icon={menuItem_1.Icon} sx={menuItem_1.menuItemSx} />
              </Box>;
        }
        return <Box key={idx} onKeyDown={stopKeyDownFromPortal}>
              {content}
              {shouldShowDivider(menuItem_1, idx) && <Box sx={{
            margin: 0
          }}>
                  <Divider />
                </Box>}
            </Box>;
      })}
      </Menu>
    </Box>;
}